/**
 * Pure utility functions for parsing bulk-upload error responses
 * in the ImportHistory context. Handles both property and order modes.
 */
import { extractValue, convertBooleanValue } from '@/util/formatting';
import { getEntityFallbackValue } from '@/services/entity-tree';

type ErrField = { value: unknown; isActive: boolean; errorMessage?: string };
type ErrFields = Record<string, ErrField>;
interface HistoryRowRef { orderPlaced: boolean; [key: string]: unknown; }
interface ParseResult { result: Record<string, unknown>[]; failedCount: number; successCount: number; }

export function parsePropertyErrors(
  data: Record<string, unknown>[],
  _tableRow: HistoryRowRef
): ParseResult {
  const result: Record<string, unknown>[] = [];
  let failedCount = 0;
  let successCount = 0;

  data.forEach((item: Record<string, unknown>, index: number) => {
    const field = (key: string, useBool = false): ErrField => {
      const raw = item[key];
      return {
        value: useBool
          ? convertBooleanValue(raw as boolean | null)
          : raw,
        isActive: false
      };
    };

    const strField = (key: string): ErrField => {
      const raw = item[key] as string | null;
      const hasErr = raw?.includes('Error') || raw === '';
      return { value: hasErr ? '-' : raw, isActive: false };
    };

    const propertyType = strField('propertyType');
    const address = strField('address1');
    const city = strField('city');
    const state = strField('state');
    const zipCode = strField('zipCode');
    const unitNumber = field('unitNumber');
    const propertyId = field('propertyId');
    const unitId = field('unitId');
    const bedrooms = field('bedrooms');
    const bathrooms = field('bathrooms');
    const squareFeet = field('squareFeet');
    const propertyStatus = field('propertyStatus');
    const portfolioName = field('portfolioName');
    const unitOccupancyStatusID = field('unitOccupancyStatusID');
    const rentReady = field('rentReady', true);
    const listingDate = field('listingDate');
    const isListed = field('isListed', true);
    const daysOnMarket = field('daysOnMarket');
    const availableDate = field('availableDate');
    const leaseSignedDate = field('leaseSignedDate');
    const leaseStartDate = field('leaseStartDate');
    const leaseEndDate = field('leaseEndDate');
    const moveInDate = field('moveInDate');
    const noticeGivenDate = field('noticeGivenDate');
    const moveOutDate = field('moveOutDate');
    const assignee = field('assignee');
    const isActive = field('isActive', true);
    const propId = field('propId');

    const entityRaw = extractValue(item.entity as string | null);
    const entity: ErrField = {
      value:
        entityRaw === '-' || entityRaw === ''
          ? getEntityFallbackValue()
          : entityRaw,
      isActive: false,
    };

    const reasons = item.errorReasons as string[] | null;
    if (reasons && reasons.length > 0) {
      failedCount += 1;
      applyPropertyErrorReasons(reasons, {
        address, city, state, zipCode, propertyType, propertyStatus,
        leaseSignedDate, propertyId, bedrooms, bathrooms, daysOnMarket,
        squareFeet, noticeGivenDate, moveInDate, moveOutDate,
        availableDate, rentReady, isListed, isActive, leaseStartDate,
        listingDate, assignee, leaseEndDate, entity
      });
    } else {
      successCount += 1;
    }

    result.push({
      id: index + 1,
      propertyType, unitNumber, address, city, state, zipCode,
      propertyId, bedrooms, bathrooms, squareFeet, unitId,
      portfolioName, propertyStatus, rentReady, listingDate, isListed,
      daysOnMarket, availableDate, leaseSignedDate, leaseStartDate,
      leaseEndDate, moveInDate, noticeGivenDate, moveOutDate,
      assignee, isActive, entity, propId,
      entityUUID: item.entityUUID,
      unitOccupancyStatusID
    });
  });

  return { result, failedCount, successCount };
}

function applyPropertyErrorReasons(reasons: string[], fields: ErrFields) {
  if (reasons.length === 1 && reasons[0]?.includes('Failed to determine location from given address')) {
    const locationFields: [string, string][] = [
      ['address', 'Not a valid address.'], ['city', 'Not a valid city.'],
      ['state', 'Not a valid state.'], ['zipCode', 'Not a valid zip code.']
    ];
    for (const [k, msg] of locationFields) {
      if (fields[k]) { fields[k].isActive = true; fields[k].errorMessage = msg; }
    }
    return;
  }

  const map: [string, string[], string, ((f: ErrField) => void)?][] = [
    ['address', ['Address', 'address1'], 'Not a valid address.'],
    ['city', ['City', 'city'], 'Not a valid city.'],
    ['state', ['State', 'state'], 'Not a valid state.'],
    ['zipCode', ['zip code', 'zipCode', 'Zip Code', 'zip', 'Zip'], 'Not a valid zip code.'],
    ['propertyType', ['property Type', 'Property Type'], 'Not a valid property type.'],
    ['propertyStatus', ['Unit Status', 'Property Status', 'property Status'], 'Limit 50 characters'],
    ['leaseSignedDate', ['Lease Signed Date', 'lease Signed Date'], 'Enter a valid date'],
    ['propertyId', ['Property Id', 'Property ID'], 'Limit 50 characters'],
    ['bedrooms', ['Bedrooms'], 'Enter a valid number'],
    ['bathrooms', ['Bathrooms'], 'Enter a valid number'],
    ['daysOnMarket', ['Days On Market'], 'Enter a valid number'],
    ['squareFeet', ['Square Feet'], 'Enter a valid number'],
    ['noticeGivenDate', ['Notice Given Date'], 'Enter a valid date'],
    ['moveInDate', ['Move In Date'], 'Enter a valid date'],
    ['moveOutDate', ['Move Out Date'], 'Enter a valid date'],
    ['availableDate', ['Available Date'], 'Enter a valid date'],
    ['rentReady', ['Rent Ready'], 'Yes or No', (f) => { f.value = null; }],
    ['isListed', ['Is Listed'], 'Yes or No', (f) => { f.value = null; }],
    ['isActive', ['Is Active'], 'Yes or No', (f) => { f.value = null; }],
    ['leaseStartDate', ['Lease Start Date'], 'Enter a valid date'],
    ['listingDate', ['Listing Date', 'listing Date'], 'Enter a valid date'],
    ['assignee', ['Assignee', 'assignee'], 'Enter a valid text'],
    ['leaseEndDate', ['Lease End Date', 'lease End Date'], 'Enter a valid date'],
    ['entity', ['Entity ID', 'Entity'], 'Not a valid entity.']
  ];

  for (const reason of reasons) {
    for (const [key, patterns, msg, extra] of map) {
      if (patterns.some((p) => reason?.includes(p)) && fields[key]) {
        fields[key].isActive = true;
        fields[key].errorMessage = msg;
        extra?.(fields[key]);
      }
    }
  }
}

export function parseOrderErrors(
  data: Record<string, unknown>[],
  tableRow: HistoryRowRef
): ParseResult {
  const result: Record<string, unknown>[] = [];
  let failedCount = 0;
  let successCount = 0;

  data.forEach((item: Record<string, unknown>, index: number) => {
    const ef = (key: string, opt = false) => ({
      value: extractValue(item[key] as string | null),
      isActive: false,
      ...(opt ? { isOptionalField: true } : {})
    });

    const services = Array.isArray(item?.services)
      ? {
          value: (item.services as string[]).map(
            (s) => extractValue(s) as string
          ),
          isActive: false
        }
      : { value: extractValue(item.services as string | null), isActive: false };

    const entityRawOrder = extractValue(item.entity as string | null);
    const entityField: ErrField = {
      value:
        entityRawOrder === '-' || entityRawOrder === ''
          ? getEntityFallbackValue()
          : entityRawOrder,
      isActive: false,
    };

    const fields: Record<string, ErrField> = {
      services,
      address: ef('address'),
      propertyType: ef('propertyType'),
      unitNumber: ef('unitNumber'),
      city: ef('city'),
      state: ef('state'),
      zipCode: { value: extractValue(item.zip as string | null), isActive: false },
      onsiteContactName: ef('onsiteContactName'),
      onsiteContactPhone: ef('onsiteContactPhone'),
      accessType: ef('accessType'),
      lockboxLocation: ef('lockboxLocation'),
      accessCode: ef('accessCode'),
      rentlyRegistrationPin: item.rentlyRegistrationPin
        ? ef('rentlyRegistrationPin')
        : { value: '-', isActive: false },
      entity: entityField,
      orderNotes: ef('orderNotes', true),
      gateCode: ef('gateCode', true),
      firstAvailableDate: ef('firstAvailableDate', true),
      propertyId: ef('propertyId', true),
      previousOrderId: ef('previousOrderId', true),
      beds: ef('beds', true),
      baths: ef('baths', true),
      squareFeet: ef('squareFeet', true)
    };

    const reasons = item.errorReasons as string[] | undefined;
    if (reasons) {
      failedCount += 1;
      applyOrderErrorReasons(reasons, fields);

      if (tableRow.orderPlaced) {
        result.push(buildOrderRow(item, index, fields));
        return;
      }
    } else {
      successCount += 1;
    }

    if (!tableRow.orderPlaced) {
      result.push(buildOrderRow(item, index, fields));
    }
  });

  return { result, failedCount, successCount };
}

function buildOrderRow(
  item: Record<string, unknown>,
  index: number,
  fields: Record<string, unknown>
) {
  return {
    id: index + 1,
    fileId: item.fileId,
    status: item.status,
    orderId: item.orderId,
    propertyPrice: item.propertyPrice,
    orderSource: item.orderSource,
    rowNumber: item.rowNumber,
    ...fields
  };
}

function applyOrderErrorReasons(
  reasons: string[],
  fields: Record<string, ErrField>
) {
  let anyActive = false;
  const errorMap: [string, string[], string][] = [
    ['services', ['services', 'Service(s)'], 'Not a valid service.'],
    ['address', ['Address', 'address1'], 'Not a valid address.'],
    ['propertyType', ['Property Type'], 'Not a valid property Type.'],
    ['city', ['City'], 'Not a valid city.'],
    ['state', ['State', 'state'], 'Not a valid state.'],
    ['zipCode', ['zip code', 'Zip', 'zip', 'zipcode', 'Zip Code'], 'Not a valid zip code.'],
    ['onsiteContactName', ['On-Site Contact Name'], 'Not a valid on-site contact name.'],
    ['onsiteContactPhone', ['On-Site Contact Phone'], 'Not a valid on-site contact phone.'],
    ['previousOrderId', ['Previous Order ID'], 'Enter a valid Previous Order ID.'],
    ['previousOrderId', ['required when ordering an Image Edit'], 'Previous Order ID is required for AI Virtual Staging (Image Edit) services.'],
    ['previousOrderId', ['only valid for Image Edit'], 'Previous Order ID applies only to AI Virtual Staging (Image Edit) services — remove it for property services.'],
    ['previousOrderId', ['must be a numeric order ID'], 'Enter a valid numeric Previous Order ID.'],
    ['services', ['cannot mix Image Edit'], "AI Virtual Staging (Image Edit) can't be combined with property services — order them separately."],
    ['accessType', ['Access Type'], 'Not a valid access type.'],
    ['lockboxLocation', ['Lockbox Location'], 'Not a valid Lockbox location.'],
    ['accessCode', ['accessCode', 'Access Code'], 'Not a valid access code.'],
    ['rentlyRegistrationPin', ['rentlyRegistrationPin', 'Rently Registration Pin'], 'Not a valid rently registration pin.'],
    ['entity', ['Entity ID', 'Entity'], 'Not a valid entity.'],
    ['propertyId', ['Property Id', 'Property ID'], 'Limit 50 characters'],
    ['orderNotes', ['orderNotes', 'Order Notes'], 'Not a valid notes'],
    ['gateCode', ['gateCode', 'Gate code', 'Gate Code'], 'Not a valid gate code'],
    ['firstAvailableDate', ['firstAvailableDate', 'Schedule Later Date', 'First Available Date', 'First available date'], 'Enter a valid date'],
    ['beds', ['Bedrooms', 'Beds'], 'Enter a valid number'],
    ['baths', ['Bathrooms', 'Baths'], 'Enter a valid number'],
    ['squareFeet', ['Square Feet'], 'Enter a valid number']
  ];

  for (const reason of reasons) {
    for (const [key, patterns, msg] of errorMap) {
      if (patterns.some((p) => reason?.includes(p)) && fields[key]) {
        fields[key].isActive = true;
        fields[key].errorMessage = msg;
        anyActive = true;
      }
    }
    if (reason?.includes('Something went wrong, please contact admin') && !anyActive) {
      const catchAll = [
        'services', 'address', 'propertyType', 'city', 'unitNumber',
        'state', 'zipCode', 'onsiteContactName', 'onsiteContactPhone',
        'accessType', 'lockboxLocation', 'accessCode', 'rentlyRegistrationPin',
        'entity'
      ];
      for (const k of catchAll) {
        if (fields[k]) {
          fields[k].isActive = true;
          const match = errorMap.find(([fk]) => fk === k);
          if (match) fields[k].errorMessage = match[2];
        }
      }
    }
  }
}
